import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { usePremiumGate } from "@/hooks/usePremiumGate";
import { getLesson, markLessonComplete, type Course, type Lesson, type LessonSummary } from "@/lib/courses";
import { LessonBody } from "@/components/LessonBody";
import { Button } from "@/components/ui/button";
import { Clock, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/courses/$courseSlug/$lessonSlug")({
  component: LessonPage,
  head: ({ params }) => ({
    meta: [
      { title: `Lesson: ${params.lessonSlug.replace(/-/g, " ")} - Inquira` },
      { name: "description", content: "A short Inquira lesson with a worked example, practice questions and a quick quiz." },
      { property: "og:title", content: "Inquira lesson" },
      { property: "og:description", content: "Learn the idea, try the practice, then check yourself with a short quiz." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function LessonPage() {
  const { courseSlug, lessonSlug } = Route.useParams();
  const { t } = useTranslation();
  const { user, isPaid } = useAuth();
  const { promptUpgrade } = usePremiumGate();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null as Course | null);
  const [lesson, setLesson] = useState(null as Lesson | null);
  const [siblings, setSiblings] = useState([] as LessonSummary[]);
  const [missing, setMissing] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    getLesson(courseSlug, lessonSlug)
      .then((loaded) => {
        if (!loaded) { setMissing(true); return; }
        setCourse(loaded.course);
        setLesson(loaded.lesson);
        setSiblings(loaded.siblings);
      })
      .catch(() => setMissing(true));
  }, [courseSlug, lessonSlug]);

  const locked = !!lesson && !lesson.is_free && !isPaid;
  const nextLesson = lesson ? siblings.find((item) => item.position === lesson.position + 1) : undefined;

  const complete = async (score: number | null) => {
    if (!user || !lesson) return;
    try {
      await markLessonComplete(user.id, lesson.id, score);
      setCompleted(true);
      toast.success(t("courses.progressSaved"));
    } catch {
      /* progress is best-effort */
    }
  };

  if (missing) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-10">
        <p className="text-muted-foreground">{t("courses.notFound")}</p>
        <Link to="/courses" className="mt-3 inline-block text-primary underline">{t("courses.allCourses")}</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 space-y-8">
      <header className="space-y-2">
        <Link to="/courses/$courseSlug" params={{ courseSlug }} className="text-sm text-primary underline">
          {course?.title}
        </Link>
        <h1 className="text-3xl font-bold">{lesson?.title}</h1>
        <p className="text-muted-foreground">{lesson?.objective}</p>
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="size-3" aria-hidden="true" />
          {t("courses.minutes", { count: lesson?.minutes ?? 0 })}
        </p>
      </header>

      {locked ? (
        <div className="space-y-4 rounded-xl border border-border bg-card p-6 text-center">
          <Lock className="mx-auto size-6 text-muted-foreground" aria-hidden="true" />
          <p className="font-semibold">{t("courses.lockedTitle")}</p>
          <p className="text-sm text-muted-foreground">{t("courses.lockedBody")}</p>
          <Button onClick={() => promptUpgrade("locked_feature", "course_lesson")}>{t("common.upgrade")}</Button>
        </div>
      ) : (
        lesson && (
          <LessonBody
            sections={lesson.sections}
            practice={lesson.practice}
            quiz={lesson.quiz}
            onQuizFinished={(score) => void complete(score)}
          />
        )
      )}

      {!locked && (
        <div className="flex flex-wrap items-center gap-3">
          {completed ? (
            <p className="flex items-center gap-2 text-sm text-primary">
              <CheckCircle2 className="size-4" aria-hidden="true" />
              {t("courses.completed")}
            </p>
          ) : (
            user && (
              <Button variant="outline" onClick={() => void complete(null)}>
                {t("courses.markComplete")}
              </Button>
            )
          )}
          {nextLesson && (
            <Button
              onClick={() =>
                void navigate({
                  to: "/courses/$courseSlug/$lessonSlug",
                  params: { courseSlug, lessonSlug: nextLesson.slug },
                })
              }
            >
              {t("courses.nextLesson")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
